import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { IconClose } from '../../assets/icons/IconClose';
import type { ModalShellProps } from './types';

const DEFAULT_PANEL_CLASSNAME = 'bg-surface-elevated ring-1 ring-line-subtle rounded-lg shadow-strong w-full max-w-md mx-l p-xl flex flex-col gap-l';

export const ModalShell: React.FC<ModalShellProps> = ({
    heading,
    ariaLabel,
    onClose,
    dismissAriaLabel,
    children,
    footer,
    panelClassName = DEFAULT_PANEL_CLASSNAME,
    rootTestId,
    titleTestId,
    dismissTestId,
}) => {
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const dismissButton = (
        <button
            type="button"
            data-testid={dismissTestId}
            onClick={onClose}
            className="p-1.5 shrink-0 text-icon hover:bg-overlay-weak rounded-full cursor-pointer transition-colors"
            aria-label={dismissAriaLabel}
        >
            <IconClose className="w-5 h-5" />
        </button>
    );

    // Portaled to the body: any transformed ancestor would otherwise become the containing block of
    // this fixed overlay and squeeze it into that ancestor's box.
    return createPortal(
        <div
            data-testid={rootTestId}
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label={heading ? undefined : ariaLabel}
        >
            <div className={panelClassName} onClick={(e) => e.stopPropagation()}>
                {heading ? (
                    // The panel's padding is p-xl: pt-xl puts the title 40px from the top, and the
                    // negative offsets pull the close button into the panel's corner.
                    <div className="relative pt-xl pr-2xl">
                        <h2 data-testid={titleTestId} className="text-l font-semibold text-content">
                            {heading}
                        </h2>
                        <div className="absolute -top-m -right-m">{dismissButton}</div>
                    </div>
                ) : (
                    <div className="absolute top-m right-m">{dismissButton}</div>
                )}

                {children}

                {footer && <div className="flex justify-end items-center gap-m">{footer}</div>}
            </div>
        </div>,
        document.body,
    );
};
